import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, type AdventureFull, type AdventureCharacter, type AdventureLocation } from '../lib/api.js';
import { AppHeader } from '../components/AppHeader.js';
import { Icon } from '../components/adventures/Icon.js';
import { iconTypeFor } from '../components/adventures/icons.js';
import { useGm } from '../hooks/useGm.js';
import '../styles/adventures.css';

type Tab = 'overview' | 'timeline' | 'map' | 'characters' | 'locations' | 'treasures' | 'flow' | 'endings';

const TAB_ORDER: Tab[] = ['overview', 'timeline', 'map', 'characters', 'locations', 'treasures', 'flow', 'endings'];

const MAP_LABELS: Record<string, string> = {
  regional_map: 'Регіональна мапа',
  adventure_map: 'Мапа пригоди',
  outpost_map: 'Аванпост',
  hq_map: 'Штаб-квартира',
  tomb_map: 'Гробниця',
};

/**
 * Перетворює внутрішні cross-link span-и (`<span class="cross-link" data-jump
 * data-target>`) на справжні `<a href>`, щоб працювали правий/середній клік
 * («відкрити в новій вкладці»). Лівий клік лишається внутрішнім переходом
 * (перехоплюється делегованим handleCrossLinkClick).
 */
function upgradeCrossLinks(html: string, slug: string): string {
  return html.replace(
    /<span class="cross-link"([^>]*)>([\s\S]*?)<\/span>/g,
    (whole, attrs: string, inner: string) => {
      const jump = /data-jump="([^"]*)"/.exec(attrs)?.[1];
      if (!jump) return whole;
      const target = /data-target="([^"]*)"/.exec(attrs)?.[1];
      const q = new URLSearchParams({ tab: jump });
      if (target) q.set('target', target);
      return `<a class="cross-link"${attrs} href="/adventures/${encodeURIComponent(slug)}?${q.toString()}">${inner}</a>`;
    },
  );
}

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Огляд',
  timeline: 'Хронологія',
  map: 'Мапа',
  characters: 'Персонажі',
  locations: 'Локації',
  treasures: 'Скарби',
  flow: 'Хід пригоди',
  endings: 'Фінали',
};

export function AdventurePage(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [adv, setAdv] = useState<AdventureFull | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  /** ID, на який треба прокрутити після зміни табу (для cross-links) */
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setAdv(null);
    setErr(null);
    if (!slug) return;
    api.adventure(slug).then(setAdv).catch((e: Error) => setErr(e.message));
  }, [slug]);

  // Початковий tab/target з query (?tab=&target=) — щоб «відкрити в новій вкладці» лендило коректно.
  useEffect(() => {
    const qTab = searchParams.get('tab');
    if (qTab && TAB_ORDER.includes(qTab as Tab)) setTab(qTab as Tab);
    const qTarget = searchParams.get('target');
    if (qTarget) setScrollTarget(qTarget);
  }, [searchParams]);

  const overviewHtml = useMemo(() => adv?.overviewHTML ? upgradeCrossLinks(adv.overviewHTML, slug) : '', [adv, slug]);
  const mapHtml = useMemo(() => adv?.mapHTML ? upgradeCrossLinks(adv.mapHTML, slug) : '', [adv, slug]);
  const flowHtml = useMemo(() => adv?.flowHTML ? upgradeCrossLinks(adv.flowHTML, slug) : '', [adv, slug]);

  // Індекс локацій (перше значуще слово назви → id) для клікабельних підписів на мапі.
  const locIndex = useMemo(
    () => (adv?.locations ?? [])
      .map((l) => ({ id: l.id, name: (l.name ?? '').toLowerCase(), first: (l.name ?? '').toLowerCase().split(/[\s—,]+/).filter(Boolean)[0] ?? '' }))
      .filter((x): x is { id: string; name: string; first: string } => Boolean(x.id && x.name)),
    [adv],
  );
  const matchLocationId = useCallback((textRaw: string): string | undefined => {
    const t = textRaw.toLowerCase().replace(/\s+/g, ' ').trim();
    if (t.length < 3) return undefined;
    let best: string | undefined; let bestLen = 0;
    for (const l of locIndex) {
      if (l.name.length >= 4 && t.includes(l.name) && l.name.length > bestLen) { best = l.id; bestLen = l.name.length; }
      else if (l.first.length >= 4 && t.includes(l.first) && l.first.length > bestLen) { best = l.id; bestLen = l.first.length; }
    }
    return best;
  }, [locIndex]);

  // Оригінальні мапи з книги (зображення, ключі *map*).
  const mapImages = useMemo(
    () => Object.entries(adv?.images ?? {}).filter(([k, v]) => /map/i.test(k) && typeof v === 'string') as Array<[string, string]>,
    [adv],
  );

  /**
   * Делегований клік: ловимо натискання на елементах із data-jump (cross-link
   * усередині raw-HTML блоків). data-jump='characters' → перемикає на той tab,
   * data-target='handelo' → потім скролимо до елемента з [data-id="handelo"].
   *
   * Також ловимо звичайні <a href="#xxx"> якщо такі є.
   */
  const handleCrossLinkClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    // Лишаємо нативну поведінку для модифікаторів/середньої кнопки/правого кліку
    // (Ctrl/Cmd-клік, середній клік → «відкрити в новій вкладці»).
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    let target = e.target as HTMLElement | null;
    while (target && target !== e.currentTarget) {
      const jump = target.dataset.jump;
      const id = target.dataset.target;
      if (jump) {
        e.preventDefault();
        const t = jump as Tab;
        if (TAB_ORDER.includes(t)) {
          setTab(t);
          if (id) setScrollTarget(id);
        }
        return;
      }
      target = target.parentElement;
    }
    // Клік на мапі/легенді без data-jump → зіставляємо підпис із назвою локації.
    const start = e.target as Element | null;
    if (start && start.closest('.map-container, .map-legend, svg')) {
      const holder = start.closest('g, .legend-item, a, text') ?? start;
      const locId = matchLocationId(holder.textContent ?? '');
      if (locId) {
        e.preventDefault();
        setTab('locations');
        setScrollTarget(locId);
      }
    }
  }, [matchLocationId]);

  // Після зміни табу й завантаження — прокрутка до елемента
  useEffect(() => {
    if (!scrollTarget) return;
    // Дамо React-у відрендерити вкладку, тоді шукаємо elem
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-id="${CSS.escape(scrollTarget)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('flash-target');
        setTimeout(() => el.classList.remove('flash-target'), 1800);
      }
      setScrollTarget(null);
    }, 80);
    return () => clearTimeout(t);
  }, [tab, scrollTarget, adv]);

  if (err) return (
    <div className="wiki-shell" style={{ gridTemplateColumns: '1fr' }}>
      <AppHeader />
      <div className="wiki-error" style={{ gridColumn: '1 / -1' }}>⚠ {err}</div>
    </div>
  );
  if (!adv) return (
    <div className="wiki-shell" style={{ gridTemplateColumns: '1fr' }}>
      <AppHeader />
      <div className="wiki-loading" style={{ gridColumn: '1 / -1' }}>завантаження…</div>
    </div>
  );

  return (
    <div className="wiki-shell" style={{ gridTemplateColumns: '1fr', gridTemplateRows: '56px auto auto 1fr' }}>
      <AppHeader />

      <header className="adv-header" style={{ gridColumn: '1 / -1' }}>
        <nav className="breadcrumb">
          <Link to="/adventures">Пригоди</Link>
          <span> / </span>
          <span>№ {adv.order}</span>
        </nav>
        <h1>{adv.meta.name ?? adv.slug}</h1>
        {adv.meta.subtitle && <div className="sub">{adv.meta.subtitle}</div>}
        <div className="meta-badges">
          {adv.meta.tier !== undefined && <span className="chip">Tier {adv.meta.tier}</span>}
          {adv.meta.levels && <span className="chip">рівні {adv.meta.levels}</span>}
          {adv.meta.duration && <span className="chip">{adv.meta.duration}</span>}
          {adv.meta.location && <span className="chip">{adv.meta.location}</span>}
          {adv.meta.type && <span className="chip">{adv.meta.type}</span>}
        </div>
      </header>

      <nav className="adv-tabs" style={{ gridColumn: '1 / -1' }}>
        {TAB_ORDER.map((t) => {
          const counts: Partial<Record<Tab, number>> = {
            characters: adv.characters.length,
            locations: adv.locations.length,
            timeline: adv.timeline.length,
            treasures: adv.treasures.length,
            endings: Array.isArray(adv.endings) ? adv.endings.length : 0,
          };
          const cnt = counts[t];
          return (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
              {cnt !== undefined && cnt > 0 && <span style={{ opacity: 0.6, marginLeft: 6 }}>{cnt}</span>}
            </button>
          );
        })}
      </nav>

      <main className="adv-body" style={{ gridColumn: '1 / -1' }} onClick={handleCrossLinkClick}>
        {tab === 'overview' && (adv.overviewHTML
          ? <div className="adv-raw-html" dangerouslySetInnerHTML={{ __html: overviewHtml }} />
          : <EmptyTab text="Огляд для цієї пригоди не задано" />)}
        {tab === 'timeline' && (adv.timeline.length
          ? <TimelineTab events={adv.timeline} />
          : <EmptyTab text="Хронологія для цієї пригоди порожня" />)}
        {tab === 'map' && ((adv.mapHTML || mapImages.length)
          ? <>
              {adv.mapHTML && <div className="adv-raw-html" dangerouslySetInnerHTML={{ __html: mapHtml }} />}
              {mapImages.length > 0 && (
                <div className="adv-map-gallery">
                  <h3>Оригінальні мапи з книги</h3>
                  <div className="map-gallery-grid">
                    {mapImages.map(([k, src]) => (
                      <figure key={k}>
                        <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={k} loading="lazy" /></a>
                        <figcaption>{MAP_LABELS[k] ?? k}</figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}
            </>
          : <EmptyTab text="Окремої мапи для цієї пригоди немає — дивись вкладку «Локації»" />)}
        {tab === 'characters' && (adv.characters.length
          ? <CharactersTab characters={adv.characters} images={adv.images} />
          : <EmptyTab text="Персонажів не описано" />)}
        {tab === 'locations' && (adv.locations.length
          ? <LocationsTab locations={adv.locations} />
          : <EmptyTab text="Локацій не описано" />)}
        {tab === 'treasures' && (adv.treasures.length
          ? <TreasuresTab treasures={adv.treasures} />
          : <EmptyTab text="Скарбів немає у списку" />)}
        {tab === 'flow' && (adv.flowHTML
          ? <div className="adv-raw-html" dangerouslySetInnerHTML={{ __html: flowHtml }} />
          : <EmptyTab text="Хід пригоди не описано" />)}
        {tab === 'endings' && (Array.isArray(adv.endings) && adv.endings.length > 0
          ? <EndingsTab endings={adv.endings as EndingItem[]} />
          : <EmptyTab text="Фінали не описано" />)}
      </main>
    </div>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────

function CharactersTab({
  characters, images,
}: { characters: AdventureFull['characters']; images: Record<string, string> }): JSX.Element {
  const [filter, setFilter] = useState<'all' | 'antagonist' | 'ally' | 'optional' | 'boss'>('all');
  const filtered = useMemo(() => {
    if (filter === 'all') return characters;
    if (filter === 'boss') return characters.filter((c) => c.boss);
    return characters.filter((c) => c.role === filter);
  }, [characters, filter]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, fontFamily: 'Spline Sans Mono, monospace', fontSize: 11 }}>
        {(['all', 'boss', 'antagonist', 'ally', 'optional'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '4px 10px',
              background: filter === f ? 'var(--gold-dim)' : 'var(--bg-card)',
              border: `1px solid ${filter === f ? 'var(--gold)' : 'var(--border)'}`,
              borderRadius: 3,
              color: filter === f ? 'var(--bg-deep)' : 'var(--text-muted)',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontSize: 10,
              fontFamily: 'inherit',
            }}
          >{f}</button>
        ))}
      </div>
      <div className="char-grid">
        {filtered.map((c, i) => (
          <CharacterCard key={c.id ?? `c-${i}`} char={c} images={images} />
        ))}
      </div>
    </div>
  );
}

function CharacterCard({ char, images }: { char: AdventureCharacter; images: Record<string, string> }): JSX.Element {
  const [open, setOpen] = useState(false);
  // Лишаємо <img> ТІЛЬКИ якщо реально маємо резолвлений шлях до файлу.
  // У hub.html для частини персонажів (xanatha_art, pl_belun, pl_ludo, pl_lestra)
  // image-ключ існує, але base64-даних під ним немає — їх не вклали при експорті.
  const portraitSrc = char.image && images[char.image] ? images[char.image] : null;
  const iconType = !portraitSrc ? iconTypeFor(char.icon) : null;
  const role = char.role ?? 'neutral';

  // Якщо немає ані фото, ані тематичної іконки — показуємо стилізовані
  // ініціали (перші 2 літери) у Cormorant Garamond, як у прототипі.
  const initials = (char.name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <article className={`char-card role-${role} ${char.boss ? 'boss' : ''}`} data-id={char.id}>
      <div className="head">
        <div className="portrait">
          {portraitSrc
            ? <img src={portraitSrc} alt={char.name ?? ''} />
            : iconType && iconType !== 'unknown'
              ? <Icon type={iconType} size={38} stroke={1.5} color="var(--gold)" />
              : <span style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 22,
                  fontWeight: 600,
                  color: 'var(--gold)',
                  letterSpacing: '0.02em',
                }}>{initials || '?'}</span>}
        </div>
        <div className="titles">
          <h3>{char.name ?? char.id ?? '—'}</h3>
          {char.epithet && <div className="epithet">{char.epithet}</div>}
          {char.type && <div className="type-row">{char.type}{char.tier !== undefined && ` · T${char.tier}`}</div>}
        </div>
      </div>

      {char.stats && (
        <div className="stat-row">
          <div className="stat"><div className="lbl">Скл.</div><div className="val">{char.stats.difficulty ?? '—'}</div></div>
          <div className="stat"><div className="lbl">HP</div><div className="val">{char.stats.hp ?? '—'}</div></div>
          <div className="stat"><div className="lbl">Stress</div><div className="val">{char.stats.stress ?? '—'}</div></div>
          <div className="stat"><div className="lbl">Атк</div><div className="val">{char.stats.attackMod ? `+${char.stats.attackMod}` : '—'}</div></div>
        </div>
      )}

      {char.description && (
        <div className="description" dangerouslySetInnerHTML={{ __html: char.description }} />
      )}

      {char.interaction && (
        <div className="char-interaction">
          <h4>Взаємодія</h4>
          <div dangerouslySetInnerHTML={{ __html: char.interaction }} />
        </div>
      )}

      <button className="reveal" onClick={() => setOpen((o) => !o)}>
        {open ? '▲ згорнути' : '▼ деталі'}
      </button>

      {open && (
        <>
          {char.attacks && char.attacks.length > 0 && (
            <div className="attacks">
              {char.attacks.map((a, i) => (
                <div key={i} className="row">
                  <span className="name">{a.name}</span>
                  {a.damage && <span className="dmg">{a.damage} {a.damageType ? `(${a.damageType})` : ''}</span>}
                  {a.description && <div className="desc" dangerouslySetInnerHTML={{ __html: a.description }} />}
                </div>
              ))}
            </div>
          )}
          {char.features && char.features.length > 0 && (
            <div className="features">
              {char.features.map((f, i) => (
                <div key={i} className="row">
                  <span className="name">{f.name}</span>
                  {f.description && <div className="desc" dangerouslySetInnerHTML={{ __html: f.description }} />}
                </div>
              ))}
            </div>
          )}
          {char.tactics && (
            <div className="tactics"><strong>Тактика:</strong> <span dangerouslySetInnerHTML={{ __html: char.tactics }} /></div>
          )}
          {char.motivation && (
            <div className="description"><strong>Мотивація:</strong> {char.motivation}</div>
          )}
        </>
      )}
    </article>
  );
}

interface LocSection {
  type?: string;       // 'text' | 'sub' | …
  name?: string;       // заголовок sub-секції
  content?: string;    // HTML тіло
  // Підтримуємо також старий формат title/body на випадок інших даних
  title?: string;
  body?: string;
}

function LocationsTab({ locations }: { locations: AdventureLocation[] }): JSX.Element {
  return (
    <div className="loc-list">
      {locations.map((l, i) => {
        const sections = (l.sections ?? []) as LocSection[];
        const iconType = iconTypeFor(l.icon);
        return (
          <article key={l.id ?? `l-${i}`} className="loc-item" data-id={l.id}>
            <h3>
              {iconType !== 'unknown' && (
                <Icon type={iconType} size={20} color="var(--gold-dim)" style={{ marginRight: 8, verticalAlign: -4 }} />
              )}
              {l.name ?? '—'}
            </h3>
            {l.sub && <div className="sub">{l.sub}</div>}
            {l.image && (
              <div className="image">
                <img src={l.image} alt={l.name ?? ''} />
                {l.imageCaption && <div className="image-caption">{l.imageCaption}</div>}
              </div>
            )}
            {sections.map((s, j) => {
              const html = s.content ?? s.body ?? '';
              const heading = s.name ?? s.title;
              const isSub = s.type === 'sub' || (heading && !s.type);
              return (
                <div key={j} className="section">
                  {isSub && heading && <h4>{heading}</h4>}
                  {html && <p dangerouslySetInnerHTML={{ __html: html }} />}
                </div>
              );
            })}
          </article>
        );
      })}
    </div>
  );
}

interface EndingItem {
  id?: string;
  icon?: string;
  title?: string;
  text?: string;
  text2?: string;
}

function EndingsTab({ endings }: { endings: EndingItem[] }): JSX.Element {
  return (
    <div className="treasure-list">
      {endings.map((e, i) => {
        const iconType = iconTypeFor(e.icon);
        return (
          <article key={e.id ?? `e-${i}`} className="treasure-item" data-id={e.id}>
            <h3>
              {iconType !== 'unknown' && (
                <Icon type={iconType} size={20} color="var(--gold-dim)" style={{ marginRight: 8, verticalAlign: -4 }} />
              )}
              {e.title ?? '—'}
            </h3>
            {e.text && <p dangerouslySetInnerHTML={{ __html: e.text }} />}
            {e.text2 && <p dangerouslySetInnerHTML={{ __html: e.text2 }} style={{ marginTop: 8, color: 'var(--text-muted)', fontStyle: 'italic' }} />}
          </article>
        );
      })}
    </div>
  );
}

function EmptyTab({ text }: { text: string }): JSX.Element {
  return (
    <div style={{
      padding: 48, textAlign: 'center',
      color: 'var(--text-dim)', fontFamily: 'Spectral, serif', fontStyle: 'italic',
    }}>{text}</div>
  );
}

function TimelineTab({ events }: { events: AdventureFull['timeline'] }): JSX.Element {
  return (
    <div className="timeline">
      {events.map((e, i) => (
        <div key={i} className={`event ${e.critical ? 'critical' : ''} ${e.recent ? 'recent' : ''}`}>
          {e.time && <div className="time">{e.time}</div>}
          <h4>{e.title ?? ''}</h4>
          {e.detail && <div className="detail" dangerouslySetInnerHTML={{ __html: e.detail }} />}
        </div>
      ))}
    </div>
  );
}

function TreasuresTab({ treasures }: { treasures: AdventureFull['treasures'] }): JSX.Element {
  return (
    <div className="treasure-list">
      {treasures.map((t, i) => (
        <TreasureCard key={t.id ?? `t-${i}`} treasure={t} />
      ))}
    </div>
  );
}

function TreasureCard({ treasure: t }: { treasure: AdventureFull['treasures'][number] }): JSX.Element {
  const iconType = iconTypeFor(t.icon);
  const body = t.desc ?? t.description;
  const { isGm } = useGm();

  return (
    <article className="treasure-item" data-id={t.id}>
      <h3>
        {iconType !== 'unknown' && (
          <Icon type={iconType} size={20} color="var(--gold-dim)" style={{ marginRight: 8, verticalAlign: -4 }} />
        )}
        {t.name ?? '—'}
      </h3>
      {t.type && <div className="sub">{t.type}</div>}
      {body && <p dangerouslySetInnerHTML={{ __html: body }} />}

      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--border)',
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        fontFamily: 'Spline Sans Mono, monospace', fontSize: 11,
      }}>
        {t.wikiSlug ? (
          <Link
            to={`/wiki/${encodeURIComponent(t.wikiSlug)}`}
            style={{
              color: 'var(--gold-bright)',
              textDecoration: 'none',
              padding: '4px 10px',
              border: '1px solid var(--gold-dim)',
              borderRadius: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            ⤳ Повний опис у вікі: {t.wikiTitle}
          </Link>
        ) : (
          <span style={{ color: 'var(--text-dim)' }}>
            Повного опису у вікі ще немає.
          </span>
        )}
        {!t.wikiSlug && isGm && t.suggestedSlug && (
          <Link
            to={`/wiki/new?slug=${encodeURIComponent(t.suggestedSlug)}&title_uk=${encodeURIComponent(t.name ?? '')}&category=Items&type=artifact&treePath=items/artifacts`}
            style={{
              color: 'var(--gold)',
              textDecoration: 'none',
              padding: '4px 10px',
              border: '1px dashed var(--gold-dim)',
              borderRadius: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            + Створити сторінку «{t.suggestedSlug}»
          </Link>
        )}
      </div>
    </article>
  );
}
